/** React imports */
import React from "react";
import { useTranslation } from "react-i18next";

/** Images */

/** MUI icons */

/** MUI imports */
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { DescriptionIconContainer } from "../AccountantProposal/DescriptionIconContainer";

/** Global variables */
const PREFIX = "ScheduleCFO";

const classes = {
    body2: `${PREFIX}-body2`,
    scheduleTitle: `${PREFIX}-scheduleTitle`,
    scheduleBox: `${PREFIX}-scheduleBox`,
    fieldset: `${PREFIX}-fieldset`,
};

const StyledContainer = styled(Box)(({ theme }) => ({
    [`& .${classes.scheduleTitle}`]: {
        fontSize: 22,
    },

    [`& .${classes.body2}`]: {
        backgroundColor: "white",
        marginTop: 15,
        [theme.breakpoints.down("md")]: {
            padding: 30,
        },
        [theme.breakpoints.up("md")]: {
            padding: 60,
        },
        minHeight: 850,
    },

    [`& .${classes.scheduleBox}`]: {
        backgroundColor: "#F8F9F8",
        marginTop: 16,
        padding: 20,
        borderRadius: 30,
    },

    [`& .${classes.fieldset}`]: {
        border: "2px solid #031851",
        borderStyle: "solid solid none solid",
        lineHeight: 0,
    },
}));

const CustomBox = styled(Box)(({ theme }) => ({
    borderRadius: "24px",
    color: "#fff",
    height: 30,
}));

const CustomBoxBar = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.secondary.main,
    borderRadius: 6,
    height: 12,
    width: "100%",
}));

const CustomTableCell = styled(TableCell)(({ theme }) => ({
    borderBottom: 0,
    paddingRight: 0,
    [`& h2`]: {
        fontSize: 12,
    },
    [`& span`]: {
        fontSize: 12,
    },
}));

export const ScheduleCFO = (props) => {
    /** Internationalization i18n */
    const [t] = useTranslation("global");

    const { cfoInfo } = props;

    /** Global variables */
    const weeksOnboarding = [
        { color: "#595aa6", label: t("proposals.week1") + " *" },
        { color: "#44469b", label: t("proposals.week2") },
        { color: "#2f3190", label: t("proposals.week3") },
    ];

    const weeksRecurring = [
        { color: "#9798c8", label: t("proposals.week3") },
        { color: "#8283bc", label: t("proposals.week4") },
        { color: "#6d6fb1", label: t("proposals.week1") },
        { color: "#595aa6", label: t("proposals.week2") },
        { color: "#44469b", label: t("proposals.week3") },
        { color: "#2f3190", label: t("proposals.week4") },
    ];

    const weeksCatchUp = [
        { color: "#9798c8", label: t("proposals.week1") + " *" },
        { color: "#8283bc", label: t("proposals.week2") },
        { color: "#6d6fb1", label: t("proposals.week3") },
        { color: "#595aa6", label: t("proposals.week4") },
        { color: "#44469b", label: t("proposals.week5") },
        { color: "#2f3190", label: t("proposals.week6") },
    ];

    return (
        <StyledContainer>
            {cfoInfo.onboarding.isActive && (
                <Box className={classes.scheduleBox}>
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow sx={{ "& th": { border: 0 } }}>
                                    <TableCell />
                                    <TableCell colSpan={7} sx={{ pt: 0, pb: 0, pr: 0 }}>
                                        <fieldset className={classes.fieldset}>
                                            <legend align="center" style={{ paddingLeft: 8, paddingRight: 8 }}>
                                                {t("proposals.project")}
                                            </legend>
                                        </fieldset>
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ "& th": { border: 0, pt: 1 } }}>
                                    <TableCell>
                                        <Typography variant="h2" sx={{ fontWeight: 700, fontSize: 12 }}>
                                            {t("proposals.onboard")}
                                        </Typography>
                                    </TableCell>
                                    {weeksOnboarding.map((week, index) => (
                                        <TableCell align="left">
                                            <CustomBox
                                                sx={{
                                                    backgroundColor: week.color,
                                                    width: "220%",
                                                    ...(index === 2 && {
                                                        pr: "14px",
                                                        pl: "14px",
                                                        width: "108%",
                                                    }),
                                                    display: "flex",
                                                    justifyContent: "space-evenly",
                                                }}
                                            >
                                                <Typography
                                                    variant="span"
                                                    sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                >
                                                    {week.label}
                                                </Typography>
                                                {index !== 2 && (
                                                    <Typography
                                                        variant="span"
                                                        sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                    >
                                                        &nbsp;
                                                    </Typography>
                                                )}
                                            </CustomBox>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">{t("proposals.intel")}</Typography>
                                    </CustomTableCell>
                                    <CustomTableCell align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">{t("proposals.design")}</Typography>
                                    </CustomTableCell>
                                    <CustomTableCell />
                                    <CustomTableCell align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">{t("proposals.setUp")}</Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={2} />
                                    <CustomTableCell align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            {cfoInfo.catchUp.isActive && (
                <Box className={classes.scheduleBox}>
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow sx={{ "& th": { border: 0 } }}>
                                    <TableCell />
                                    <TableCell colSpan={7} sx={{ pt: 0, pb: 0, pr: 0 }}>
                                        <fieldset className={classes.fieldset}>
                                            <legend align="center" style={{ paddingLeft: 8, paddingRight: 8 }}>
                                                {t("proposals.project")}
                                            </legend>
                                        </fieldset>
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ "& th": { border: 0, pt: 1 } }}>
                                    <TableCell>
                                        <Typography variant="h2" sx={{ fontWeight: 700, fontSize: 12 }}>
                                            Catch-up
                                        </Typography>
                                    </TableCell>
                                    {weeksCatchUp.map((week, index) => (
                                        <TableCell align="left">
                                            <CustomBox
                                                sx={{
                                                    backgroundColor: week.color,
                                                    width: sessionStorage.getItem("lng") === "en" ? "240%" : "260%",
                                                    ...(index === 5 && {
                                                        pr: sessionStorage.getItem("lng") === "en" ? "17px" : "21px",
                                                        pl: sessionStorage.getItem("lng") === "en" ? "17px" : "21px",
                                                        width:
                                                            sessionStorage.getItem("lng") === "en"
                                                                ? "119%"
                                                                : "120% !important",
                                                    }),
                                                    display: "flex",
                                                    justifyContent: "space-evenly",
                                                }}
                                            >
                                                <Typography
                                                    variant="span"
                                                    sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                    noWrap
                                                >
                                                    {week.label}
                                                </Typography>
                                                {index !== 5 && (
                                                    <Typography
                                                        variant="span"
                                                        sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                    >
                                                        &nbsp;
                                                    </Typography>
                                                )}
                                            </CustomBox>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <DescriptionIconContainer key={"proposals.budForCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.budForCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.finModellingCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.finModellingCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.projManagCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.projManagCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.kpiMonitoringCfo"} top={28.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.kpiMonitoringCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={2} />
                                    <CustomTableCell colSpan={4} align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.personalizedReporting"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.personalizedReporting")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={2} />
                                    <CustomTableCell colSpan={4} align="right">
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            {cfoInfo.recurring.isActive && (
                <Box className={classes.scheduleBox}>
                    <TableContainer sx={{ overflowX: "auto" }}>
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow sx={{ "& th": { border: 0 } }}>
                                    <TableCell />
                                    <TableCell colSpan={2} sx={{ pt: 0, pb: 0, pr: 0 }}>
                                        <fieldset className={classes.fieldset}>
                                            <legend align="center" style={{ paddingLeft: 8, paddingRight: 8 }}>
                                                {t("proposals.eachMonth")}
                                            </legend>
                                        </fieldset>
                                    </TableCell>
                                    <TableCell colSpan={4} sx={{ pt: 0, pb: 0, pr: 0 }}>
                                        <fieldset className={classes.fieldset}>
                                            <legend align="center" style={{ paddingLeft: 8, paddingRight: 8 }}>
                                                {t("proposals.nextMonth")}
                                            </legend>
                                        </fieldset>
                                    </TableCell>
                                </TableRow>
                                <TableRow sx={{ "& th": { border: 0, pt: 1 } }}>
                                    <TableCell>
                                        <Typography variant="h2" sx={{ fontWeight: 700, fontSize: 12 }}>
                                            {t("proposals.recurring2")}
                                        </Typography>
                                    </TableCell>
                                    {weeksRecurring.map((week, index) => (
                                        <TableCell align="left">
                                            <CustomBox
                                                sx={{
                                                    backgroundColor: week.color,
                                                    width: sessionStorage.getItem("lng") === "en" ? "240%" : "260%",
                                                    ...(index === 5 && {
                                                        pr: sessionStorage.getItem("lng") === "en" ? "17px" : "21px",
                                                        pl: sessionStorage.getItem("lng") === "en" ? "17px" : "21px",
                                                        width: "118%",
                                                    }),
                                                    display: "flex",
                                                    justifyContent: "space-evenly",
                                                }}
                                            >
                                                <Typography
                                                    variant="span"
                                                    sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                    noWrap
                                                >
                                                    {week.label}
                                                </Typography>
                                                {index !== 5 && (
                                                    <Typography
                                                        variant="span"
                                                        sx={{ display: "flex", alignItems: "center", fontSize: 12 }}
                                                    >
                                                        &nbsp;
                                                    </Typography>
                                                )}
                                            </CustomBox>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <DescriptionIconContainer key={"proposals.budForCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.budForCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell align="right" colSpan={2}>
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.finModellingCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.finModellingCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell align="right" colSpan={2}>
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.projManagCfo"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.projManagCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} />
                                    <CustomTableCell align="right" colSpan={2}>
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.kpiMonitoringCfo"} top={28.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.kpiMonitoringCfo")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} />
                                    <CustomTableCell align="right" colSpan={2}>
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                                <DescriptionIconContainer key={"proposals.personalizedReporting"} top={22.5} />
                                <TableRow>
                                    <CustomTableCell component="th" scope="row">
                                        <Typography variant="h2">
                                            {" " + t("proposals.personalizedReporting")}
                                        </Typography>
                                    </CustomTableCell>
                                    <CustomTableCell colSpan={4} />
                                    <CustomTableCell align="right" colSpan={2}>
                                        <CustomBoxBar />
                                    </CustomTableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
            <Typography variant="body1" sx={{ pt: 2, pl: 2, fontSize: 13 }}>
                * {t("proposals.weekOneBegins")}
            </Typography>
        </StyledContainer>
    );
};
